/**
 * Utility class for managing body scroll behavior when modals are open.
 *
 * Provides reference counting to handle multiple modals being open simultaneously.
 * When any modal is open, body scrolling is disabled. When all modals are closed,
 * body scrolling is restored.
 *
 * @example
 * ```typescript
 * // In modal component connectedCallback or open handler
 * BodyScrollManager.disableScroll();
 *
 * // In modal component disconnectedCallback or close handler
 * BodyScrollManager.enableScroll();
 * ```
 */
export class BodyScrollManager {
  private static scrollDisabledCount = 0;
  private static scrollY = 0;
  private static isIOS =
    /iP(ad|hone|od)/.test(navigator.platform) || (navigator.userAgent.includes("Mac") && "ontouchend" in document);

  private static originalStyles: { overflow?: string; position?: string; top?: string; left?: string; right?: string } =
    {};

  /**
   * Disables body scrolling by setting overflow: hidden.
   * Uses reference counting to support multiple modals.
   */
  static disableScroll(): void {
    if (this.scrollDisabledCount === 0) {
      if (this.isIOS) {
        // iOS Safari workaround
        this.scrollY = window.scrollY;
        this.originalStyles = {
          overflow: document.body.style.overflow,
          position: document.body.style.position,
          top: document.body.style.top,
          left: document.body.style.left,
          right: document.body.style.right,
        };

        // Lock body in place
        document.body.style.position = "fixed";
        document.body.style.top = `-${this.scrollY}px`;
        document.body.style.left = "0";
        document.body.style.right = "0";
        document.body.style.overflow = "hidden";
      } else {
        // Normal browsers
        this.originalStyles.overflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
      }
    }
    this.scrollDisabledCount++;
  }

  /**
   * Enables body scrolling when no modals require it to be disabled.
   * Uses reference counting to support multiple modals.
   */
  static enableScroll(): void {
    this.scrollDisabledCount = Math.max(0, this.scrollDisabledCount - 1);
    if (this.scrollDisabledCount === 0) {
      if (this.isIOS) {
        // Restore scroll state
        document.body.style.position = this.originalStyles.position || "";
        document.body.style.top = this.originalStyles.top || "";
        document.body.style.left = this.originalStyles.left || "";
        document.body.style.right = this.originalStyles.right || "";
        document.body.style.overflow = this.originalStyles.overflow || "";

        // Restore scroll position
        window.scrollTo(0, this.scrollY);
      } else {
        document.body.style.overflow = this.originalStyles.overflow || "unset";
      }

      this.originalStyles = {};
    }
  }

  /**
   * Force reset scroll state (useful for cleanup in edge cases).
   * Should generally not be needed in normal operation.
   */
  static forceEnableScroll(): void {
    this.scrollDisabledCount = 0;
    if (this.isIOS) {
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
      document.body.style.overflow = "";
      if (this.scrollY > 0) window.scrollTo(0, this.scrollY);
    } else {
      document.body.style.overflow = "";
    }
  }

  /**
   * Get current disabled count (useful for debugging).
   */
  static getDisabledCount(): number {
    return this.scrollDisabledCount;
  }
}
